import React, { FC, useCallback, useMemo } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Box from "../../Unknown/Box";
import CircularProgress from "../../Unknown/CircularProgress";
import Typography from "../../Unknown/Typography";
import { useAppContext } from "../../Unknown/AppContext";
import useCustomer from "../CustomerInformationDrawerContainer/useCustomer";
import MergeCustomersContainer from "../MergeCustomers";
import PatientInformationDrawerContainer from "../../Patient/PatientInformationDrawerContainer";
import useMergeScreenPatientDrawer from "../../Patient/PatientInformationDrawerContainer/useMergeScreenPatientDrawer";

const MERGE_ALLOWED_ROLES = ["regional_admin", "super_admin"] as const;

// Route-level screen for ":role/.../customer/:customerId/merge" (see
// AuthenticatedRoot) — renders as a full page (Figma node 11427:31220),
// not a modal, so it gets the shared app header for free via
// AuthenticatedLayout wrapping every route.
const MergeCustomersScreen: FC = () => {
  const { customerId, role } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { databaseUserData } = useAppContext();

  // These are flat sibling routes (not nested Route elements), so relative
  // "../" navigation isn't reliable here — strip the "/merge" suffix explicitly.
  const parentPath = location.pathname.replace(/\/merge\/?$/, "");

  const { isLoading, error, data: customer } = useCustomer({ customerId });

  const canMerge = MERGE_ALLOWED_ROLES.some((allowedRole) =>
    databaseUserData.permissions.includes(allowedRole),
  );

  // AC19 — Cancel returns to wherever the merge screen was opened from.
  const handleDone = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // AC33/34 — "View customer" on the success view must go to the SELECTED
  // customer's drawer specifically, not back(-1): the merge screen may have
  // been opened from a customer who ended up non-selected (merge_status =
  // 'merged'), and that drawer no longer has any linked data to show.
  const handleViewCustomer = useCallback(
    (selectedCustomerId: string) => {
      const viewCustomerPath = parentPath.replace(
        /\/customer\/[^/]+$/,
        `/customer/${selectedCustomerId}`,
      );
      navigate(viewCustomerPath, { replace: true });
    },
    [navigate, parentPath],
  );

  // AC12 — footer "Wards"/"Family members" links, and the patient links in
  // the duplicate-group cards, open the Patient drawer ON TOP of this screen
  // (see useMergeScreenPatientDrawer): navigating to the "patient/:mrn"
  // route instead would unmount this screen and lose the merge in progress.
  const { isPatientDrawerOpen, openPatientDrawer, patientDrawerProps } =
    useMergeScreenPatientDrawer();

  // AC38 — conflicting-appointment links on the duplicate-group "Conflict"
  // card open the appointment in a new tab (unlike handlePatientClick's
  // in-place navigation), since the merge screen itself stays open behind it.
  const handleAppointmentClick = useCallback(
    (appointmentId: string) => {
      window.open(`/${role}/appointment/${appointmentId}`, "_blank");
    },
    [role],
  );

  const initialCustomer = useMemo(() => {
    if (!customer) return null;
    return {
      id: customer.id,
      firstName: customer.first_name,
      lastName: customer.last_name,
      email: customer.email,
      phone: customer.mobile_phone_number,
      additionalPhone: customer.additional_phone_number,
      wards: (customer.patients ?? []).map((p) => ({
        id: p.id,
        firstName: p.first_name,
        lastName: p.last_name,
        mrn: p.mrn,
      })),
      // getCustomerData fetches wards/family via two independent FK queries
      // (patients!legal_guardian_id / patients!family_contact_id), so a
      // patient with BOTH FKs set to this customer (legal_guardian_id ===
      // family_contact_id) shows up in both lists. Per the app's
      // ward/family classification (see ticket.md), that combination counts
      // as a ward, not a family member — exclude anything already in wards.
      familyMembers: (customer.familyMembers ?? [])
        .filter((p) => !customer.patients?.some((w) => w.id === p.id))
        .map((p) => ({
          id: p.id,
          firstName: p.first_name,
          lastName: p.last_name,
          mrn: p.mrn,
        })),
    };
  }, [customer]);

  if (!canMerge) {
    return <Navigate replace to={parentPath} />;
  }

  if (isLoading || !initialCustomer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <MergeCustomersContainer
        initialCustomer={initialCustomer}
        onDone={handleDone}
        onViewCustomer={handleViewCustomer}
        onPatientClick={openPatientDrawer}
        onAppointmentClick={handleAppointmentClick}
      />
      {isPatientDrawerOpen && (
        <PatientInformationDrawerContainer {...patientDrawerProps} />
      )}
    </>
  );
};

export default MergeCustomersScreen;
